import { Box, Stack, Typography, useMediaQuery } from '@mui/material'
import { CheckboxLabel } from 'components/CheckboxLabel/CheckboxLabel'
import { InputWithDropdown } from 'components/InputWithDropdown/InputWithDropdown'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import { SaveBtn } from 'components/SaveBtn/SaveBtn'
import { Slider } from 'components/Slider/Slider'
import { Form, Formik, useFormikContext } from 'formik'
import { useCodeList } from 'hooks/useCodeList'
import { usePutMasterProduct } from 'hooks/useMasterProducts'
import i18n from 'lib/i18'
import { contentLanguages } from 'lib/languages'
import theme from 'lib/theme'
import { useAlert } from 'providers/AlertProvider'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { LanguageSelector } from './LanguageSelector'
import { useProductInfo } from './Product'
import { formatLangContentField, formatLangContentListField } from './utils'

type FormikFieldType = {
  functionalName: {
    options: { [lang: string]: string }
    contentLanguage: string
  }
  variantName: {
    options: { [lang: string]: string }
    contentLanguage: string
  }
  searchKeywords: {
    options: { [lang: string]: string }
    contentLanguage: string
  }
  boostValue: number
  dietTypes: { dietTypeCode: string; dietTypeSubcode?: string }[]
}

const SearchFields = () => {
  const { t } = useTranslation()
  const { values } = useFormikContext()

  const inputWithDropdowns = [
    {
      label: t('functionalName'),
      fieldName: 'functionalName',
      fieldValue: (values as FormikFieldType).functionalName,
    },
    {
      label: t('variantName'),
      fieldName: 'variantName',
      fieldValue: (values as FormikFieldType).variantName,
    },
    {
      label: t('searchKeywords'),
      fieldName: 'searchKeywords',
      fieldValue: (values as FormikFieldType).searchKeywords,
      helperText: t('searchKeywordsHelperText'),
      multiline: true,
    },
  ]

  return (
    <Stack mb={6} ml={1} mt={3} spacing={3}>
      {inputWithDropdowns.map((input) => {
        return (
          <InputWithDropdown
            key={input.fieldName}
            multiline={input.multiline ?? false}
            helperText={input.helperText || ''}
            label={input.label}
            value={input.fieldValue.options[input.fieldValue.contentLanguage]}
            selectedOption={input.fieldValue.contentLanguage}
            dropdownFieldName={`${input.fieldName}.contentLanguage`}
            inputFieldName={`${input.fieldName}.options`}
            inputOptions={input.fieldValue.options}
          />
        )
      })}
    </Stack>
  )
}

export const ProductSearchSettings = () => {
  const {
    codeListTypes: dietTypes,
    error,
    isLoading,
  } = useCodeList('dietTypeCode')
  const { t } = useTranslation()
  const { setAlert } = useAlert()
  const { masterProductId } = useParams()
  const { masterProduct, product } = useProductInfo()
  const { mutateAsync, isLoading: isLoadingPutRequest } = usePutMasterProduct(
    masterProductId as string
  )
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const initialValues = {
    functionalName: {
      options: product.functionalName,
      contentLanguage: contentLanguages[0],
    },
    variantName: {
      options: product.variantName,
      contentLanguage: contentLanguages[0],
    },
    selectedLanguage: contentLanguages[0],
    searchKeywords: {
      options: product.searchKeywords,
      contentLanguage: contentLanguages[0],
    },
    boostValue: product.boostValue,
    dietTypes: product.dietTypes,
  }
  const handleContentLanguageChange = (
    value: string,
    setFieldValue: (field: string, value: string) => void
  ) => {
    const fieldsToChange = ['functionalName', 'variantName', 'searchKeywords']
    fieldsToChange.forEach((field) => {
      setFieldValue(`${field}.contentLanguage`, value)
    })
    setFieldValue('selectedLanguage', value)
  }
  const handleSubmit = async (values: Partial<FormikFieldType>) => {
    const masterProductCopy = { ...masterProduct }
    const temp = masterProductCopy.tradeItem.tradeItemInformation.extensions
    const functionalName = values.functionalName
      ? formatLangContentField(values.functionalName.options)
      : null
    const variantName = values.variantName
      ? formatLangContentField(values.variantName.options)
      : null
    const searchKeywords = values.searchKeywords
      ? formatLangContentListField(values.searchKeywords.options)
      : null
    const editedDietTypes =
      values.dietTypes && values.dietTypes?.length > 0
        ? values.dietTypes.map((checkedDietType) => {
            if (
              product.dietTypes
                .map((dietType) => dietType.dietTypeCode)
                .includes(checkedDietType.dietTypeCode)
            ) {
              return product.dietTypes.filter(
                (dietType) =>
                  dietType.dietTypeCode === checkedDietType.dietTypeCode
              )[0]
            }
            return checkedDietType
          })
        : null

    temp.tradeItemDescriptionModule.tradeItemDescriptionInformation = {
      ...temp.tradeItemDescriptionModule.tradeItemDescriptionInformation,
      ...(functionalName && { functionalName }),
      ...(variantName && { variantDescription: variantName }),
    }
    temp.marketingInformationModule = {
      ...temp.marketingInformationModule,
      marketingInformation: {
        ...temp.marketingInformationModule?.marketingInformation,
        ...(searchKeywords && { tradeItemKeyWords: searchKeywords }),
      },
    }
    temp.dgPresentationModule = {
      ...temp.dgPresentationModule,
      searchWeightFactor0: values.boostValue as number,
    }
    temp.dietInformationModule = {
      ...temp.dietInformationModule,
      dietInformation: {
        ...temp.dietInformationModule?.dietInformation,
        dietTypeInformation: editedDietTypes || [],
      },
    }

    await mutateAsync(masterProductCopy, {
      onSuccess: () => {
        setAlert(t('saved'), 'success', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
      },
      onError: () => {
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
      },
    })
  }

  const handleCheckboxChange = (
    selectedDietTypes: { dietTypeCode: string; dietTypeSubcode?: string }[],
    setFieldValue: (
      field: string,
      value: { dietTypeCode: string; dietTypeSubcode?: string }[]
    ) => void,
    diet: { code: string }
  ) => {
    const temp = selectedDietTypes
      .map((dietInfo) => dietInfo.dietTypeCode)
      .includes(diet.code)
      ? selectedDietTypes.filter(
          (dietType) => dietType.dietTypeCode !== diet.code
        )
      : [...selectedDietTypes, { dietTypeCode: diet.code }]

    setFieldValue('dietTypes', temp)
  }

  if (isLoading) return <LoadingIndicator />
  if (error) return <h1>Error</h1>
  return (
    <Box maxWidth='500px'>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={async (values) => handleSubmit(values)}>
        {({ dirty, values, setFieldValue }) => (
          <Form>
            {contentLanguages?.length > 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: theme.spacing(2),
                  right: theme.spacing(2),
                  zIndex: 1000,
                }}>
                <LanguageSelector
                  selectedValue={values.selectedLanguage}
                  handleChange={handleContentLanguageChange}
                />
              </Box>
            )}
            <Typography variant='body2' mt={3}>
              {t('searchFields')}
            </Typography>
            <SearchFields />
            <Typography variant='body2' mt={3}>
              {t('searchWeight')}
            </Typography>
            <Box pl={1}>
              <Slider
                label={t('boostValue')}
                ariaLabel={t('boostValue')}
                maxValue={0.99}
                minValue={0.1}
                name='boostValue'
                step={0.01}
                value={values.boostValue}
                setFieldValue={setFieldValue}
              />
            </Box>
            <Typography variant='body2' mt={3}>
              {t('dietSuitableFor')}
            </Typography>
            <Box pl={1} mb={8}>
              {dietTypes.map(
                (diet: {
                  code: string
                  label: { $: string; '@languageCode': string }[]
                }) => {
                  const label =
                    diet.label.find(
                      (dietLabel) =>
                        dietLabel['@languageCode'] === i18n.language
                    ) ||
                    diet.label.find(
                      (dietLabel) =>
                        dietLabel['@languageCode'] === contentLanguages[0]
                    )
                  return (
                    <CheckboxLabel
                      key={diet.code}
                      value={Boolean(
                        values.dietTypes?.length > 0 &&
                          values.dietTypes
                            .map(
                              (checkedDietTypes) =>
                                checkedDietTypes.dietTypeCode
                            )
                            .includes(diet.code)
                      )}
                      label={label?.$ || ''}
                      handleChange={() =>
                        handleCheckboxChange(
                          values.dietTypes,
                          setFieldValue,
                          diet
                        )
                      }
                    />
                  )
                }
              )}
            </Box>
            <Box
              mt={3}
              sx={{
                position: 'fixed',
                bottom: isMobile ? '0px' : theme.spacing(2),
                width: isMobile ? '92%' : '504px',
                zIndex: 1000,
              }}>
              <SaveBtn
                isValid
                dirty={dirty}
                showOverlay
                isLoadingRequest={isLoadingPutRequest}
              />
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  )
}
